import type { ApiAsset, ApiTitle } from "@lumi/data";

/**
 * Type for the authenticated data client
 * (return type of createClient from @lumi/data)
 */
type DataClient = {
  listTitles: () => Promise<ApiTitle[]>;
  listTitleAssets: (titleId: string) => Promise<ApiAsset[]>;
};

/**
 * Simple in-memory cache for preloaded player data
 * This allows title data fetched during transition to be reused by Player.tsx
 */
interface PreloadedPlayerData {
  title: ApiTitle;
  asset: ApiAsset;
  timestamp: number;
}

const preloadCache = new Map<string, PreloadedPlayerData>();
const CACHE_TTL = 10000; // 10 seconds - data is only valid during transition

/**
 * Preload title and asset data for the player
 * This should be called during page transition to warm the cache
 */
export async function preloadPlayerData(
  titleSlugOrId: string,
  authenticatedClient: DataClient,
  options?: {
    assetId?: string;
    assetKind?: string;
  },
): Promise<void> {
  console.log("🚀 Starting player data preload for:", titleSlugOrId);

  try {
    // Load title
    const titles = await authenticatedClient.listTitles();
    const title = titles.find((t: ApiTitle) => t.id === titleSlugOrId || t.slug === titleSlugOrId);

    if (!title) {
      console.warn("⚠️ Title not found during preload:", titleSlugOrId);
      return;
    }

    // Load assets
    const assets = await authenticatedClient.listTitleAssets(title.id);

    if (!assets || assets.length === 0) {
      console.warn("⚠️ No assets found during preload");
      return;
    }

    // Select appropriate asset
    let selectedAsset: ApiAsset;
    if (options?.assetId) {
      selectedAsset = assets.find((a: ApiAsset) => a.id === options.assetId) || assets[0];
    } else if (options?.assetKind) {
      selectedAsset = assets.find((a: ApiAsset) => a.kind === options.assetKind) || assets[0];
    } else {
      selectedAsset = assets.find((a: ApiAsset) => a.kind === "FEATURE") || assets[0];
    }

    const cachedData = {
      title,
      asset: selectedAsset,
      timestamp: Date.now(),
    };

    // Cache by BOTH slug and ID so lookups work either way
    const slugKey = `${title.slug}-${options?.assetId || options?.assetKind || "default"}`;
    const idKey = `${title.id}-${options?.assetId || options?.assetKind || "default"}`;
    preloadCache.set(slugKey, cachedData);
    preloadCache.set(idKey, cachedData);
  } catch (error) {
    console.error("❌ Failed to preload player data:", error);
    // Non-fatal - Player.tsx will load data normally
  }
}

/**
 * Get preloaded player data from cache
 * Returns null if not in cache or expired
 */
export function getPreloadedPlayerData(
  titleSlugOrId: string,
  options?: {
    assetId?: string;
    assetKind?: string;
  },
): PreloadedPlayerData | null {
  const cacheKey = `${titleSlugOrId}-${options?.assetId || options?.assetKind || "default"}`;
  const cached = preloadCache.get(cacheKey);

  if (!cached) {
    return null;
  }

  // Check if expired
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    preloadCache.delete(cacheKey);
    console.log("🗑️ Preloaded data expired:", cacheKey);
    return null;
  }

  return cached;
}

/**
 * Clear a specific cache entry or all entries
 */
export function clearPreloadCache(titleSlugOrId?: string): void {
  if (titleSlugOrId) {
    for (const key of preloadCache.keys()) {
      if (key.startsWith(titleSlugOrId)) {
        preloadCache.delete(key);
      }
    }
  } else {
    preloadCache.clear();
  }
}
